import type { FundDetail, Holding } from "@/data/fund-detail-types";
/**
 * Live holdings for a single fund.
 *
 * Fetches from `GET /api/data/holdings?ticker=X` (Postgres-backed,
 * Cache-Control: no-store). Returns an empty snapshot when no holdings have
 * been ingested yet; consumers render their static or empty-state path.
 */
import { apiFetch } from "@/lib/api-client";
import { useQuery } from "@tanstack/react-query";

// Raw row shape returned by the holdings API endpoint.
interface HoldingRow {
  security_name: string;
  security_cusip: string;
  security_ticker: string;
  weight_pct: string | number | null;
  market_value: string | number | null;
  shares: string | number | null;
}

interface LiveHoldingsState {
  holdings: Holding[];
  holdingsCount: number;
  dateLabel: string;
  isLive: boolean;
  isLoading: boolean;
  aum: number | null;
  asOfDate: string | null;
}

function toHolding(row: HoldingRow): Holding {
  const pct = row.weight_pct != null ? Number(row.weight_pct) : null;
  return {
    ticker: row.security_ticker,
    name: row.security_name,
    sector: "",
    pct: Number.isFinite(pct as number) ? pct : null,
    marketValue: row.market_value != null ? String(row.market_value) : "",
    cusip: row.security_cusip || undefined,
  };
}

export function useLiveHoldings(fund: FundDetail): LiveHoldingsState {
  const ticker = fund.ticker;

  const { data, isPending } = useQuery({
    queryKey: ["holdings", ticker],
    queryFn: () => apiFetch<HoldingRow[]>(`/api/data/holdings?ticker=${ticker}`),
    enabled: Boolean(ticker),
    staleTime: 0, // always fresh — ingestion can update intraday
    retry: false,
  });

  // No live data yet — fall back to static fund holdings.
  if (!data || data.length === 0) {
    return {
      holdings: fund.holdings,
      holdingsCount: fund.holdingsCount,
      dateLabel: fund.dateLabel,
      isLive: false,
      isLoading: isPending,
      aum: null,
      asOfDate: null,
    };
  }

  const holdings = data.map(toHolding);
  return {
    holdings,
    holdingsCount: holdings.length,
    dateLabel: "Latest",
    isLive: true,
    isLoading: false,
    aum: null,
    asOfDate: null,
  };
}
